import React from 'react';
import { GuideLayout, GuideList, GuideSteps, GuideTip } from '../components/GuideLayout';

const sections = [
  {
    id: 'getting-started',
    title: 'Getting Started & Signing In',
    body: (
      <>
        <p>
          Benchmark PH is invite-only. Your organisation's administrator at IBPAP issues you two codes:
          a <strong>Company Code</strong> (5 characters, e.g. <code>ACM13</code>) and an{' '}
          <strong>Invite Code</strong> (6 characters, e.g. <code>T5BGLF</code>).
        </p>
        <GuideSteps steps={[
          'Open the sign-in page from "Get Started" on the home page.',
          'Enter your Company Code and Invite Code — you can paste them; spaces, dashes and lowercase are cleaned up automatically.',
          'Click Sign In. You will land on the questionnaire (or the compliance step on your first visit).',
        ]} />
        <GuideTip>If you see "Invalid company code or invite code", double-check both codes with your administrator — codes can be regenerated, which invalidates older ones.</GuideTip>
      </>
    ),
  },
  {
    id: 'compliance',
    title: 'Compliance Acceptance (First Visit)',
    body: (
      <>
        <p>
          Before your first survey session you must read and accept the <strong>Member Acceptance Policy</strong>{' '}
          and the <strong>Privacy Notice</strong>. Tick both checkboxes, then click{' '}
          <em>I Accept — Proceed to Survey</em>.
        </p>
        <GuideList items={[
          'Your acceptance is recorded with a date and time for audit purposes.',
          'You only accept once — the gate will not appear on later visits.',
          'A signed copy of your acceptance is included in your downloadable report (see Section 9).',
        ]} />
      </>
    ),
  },
  {
    id: 'navigation',
    title: 'Navigating the Questionnaire',
    body: (
      <>
        <GuideList items={[
          'The survey has four sections: A (Organisational Profile), B (Double Materiality Assessment), C (Quantitative KPIs) and D (Forward-Looking Insights).',
          'Jump between sections using the Section A–D pills at the top, or the Previous / Next Section buttons at the bottom.',
          'The green bar under the header shows your position in the survey.',
          'Hover (or tap) any question label to see a tooltip with definitions, data sources and formulas.',
        ]} />
      </>
    ),
  },
  {
    id: 'section-a',
    title: 'Section A — Organisational Profile',
    body: (
      <>
        <GuideList items={[
          'A1 Total Employees: full-time equivalent headcount at year-end.',
          'A2 Business Segments: select all that apply — GCC, CX and Contact Center, Healthcare, KPO, IT & Software Development, Animation and Creative Services, Game Development, and the Support Services sub-group (Real Estate, Telecommunications, Human Resources, Energy, Financial Services).',
          'A3 Operational Sites: number of delivery centers and major facilities in the Philippines.',
        ]} />
      </>
    ),
  },
  {
    id: 'section-b',
    title: 'Section B — Double Materiality Assessment',
    body: (
      <>
        <p>
          For each of the 13 ESG issues, rate two dimensions on a 1–5 scale:{' '}
          <strong>Financial Risk/Opportunity</strong> (how much the issue affects your business) and{' '}
          <strong>Social/Environmental Impact</strong> (how much your business affects people and planet).
        </p>
        <GuideList items={[
          'Scale: 1 = Low relevance, 3 = Moderate, 5 = High / Critical.',
          'A selected rating turns solid green with a ✓ badge — nothing is pre-selected, every rating is your active choice.',
          'Use the AI Assist button on any issue for plain-language explanations and examples.',
        ]} />
      </>
    ),
  },
  {
    id: 'section-c',
    title: 'Section C — Quantitative KPI Inputs',
    body: (
      <>
        <p>
          Enter your organisation's numbers for privacy, talent, energy/emissions and community KPIs.
          Grey italic text like <em>e.g., 18</em> is only an example — your entered values appear in dark bold text.
        </p>
        <GuideList items={[
          'Percentage fields accept 0–100 only; out-of-range entries are corrected automatically.',
          '3.3 Scope 1 & 2 Emissions: if you have entered your electricity consumption (3.1), the form offers a one-click estimate (kWh × 0.0007, PH grid factor). Tick "Emission data not available" if you genuinely have no data.',
          'Tooltips on every KPI show the data source and formula to use.',
        ]} />
      </>
    ),
  },
  {
    id: 'section-d',
    title: 'Section D — Forward-Looking Insights',
    body: (
      <>
        <GuideList items={[
          'D1: indicate whether your organisation has ESG targets. Answering "No" disables D2 automatically.',
          'D2: your 2030 carbon reduction target in percent (only if D1 is "Yes").',
          'D3: pick your top 3 ESG risks in priority order — numbered badges show your ranking; use Reset to start over.',
          'Risks marked "★ rated high in Section B" match issues you rated 4–5, helping you keep your answers consistent.',
        ]} />
      </>
    ),
  },
  {
    id: 'ai-advisor',
    title: 'ESG AI Advisor',
    body: (
      <>
        <p>
          The floating chat button (bottom-right) opens the ESG AI Advisor. Ask it anything about the survey:
          definitions, how to compute a KPI, where to find data in your organisation, or what a rating means.
        </p>
        <GuideTip>The Advisor guides you but never answers on your behalf — all responses you submit are your own.</GuideTip>
      </>
    ),
  },
  {
    id: 'saving-submitting',
    title: 'Saving Progress & Submitting',
    body: (
      <>
        <GuideList items={[
          'Save Progress (header button) stores your answers anytime — you can sign out and continue later with the same codes.',
          'Submit Questionnaire appears on Section D. Before submitting, a Consistency Check reviews your answers.',
          'The "Review before you submit" window lists blank answers and inconsistencies (e.g., a top risk you rated low in Section B). Use the Section links to jump straight to an item, or choose Submit Anyway.',
          'After submitting you receive a Response Code (e.g., RSP-XXXXXX) — keep it as your submission reference.',
        ]} />
      </>
    ),
  },
  {
    id: 'report',
    title: 'Your Benchmark Report',
    body: (
      <>
        <p>
          After submission you are taken to your report page (also available via <em>View Report</em> in the header).
        </p>
        <GuideList items={[
          'Compare your materiality ratings and KPIs against anonymised industry averages.',
          'Download Report generates a PDF containing your signed compliance acceptance, your full response and the cohort benchmark.',
        ]} />
      </>
    ),
  },
  {
    id: 'editing',
    title: 'Editing After Submission',
    body: (
      <>
        <GuideList items={[
          'You can reopen the questionnaire and edit answers after submitting.',
          'Every change is logged against your original submission for transparency; your Response Code stays the same.',
          'Edited submissions pass through the same Consistency Check.',
        ]} />
      </>
    ),
  },
  {
    id: 'faq',
    title: 'Troubleshooting & FAQ',
    body: (
      <>
        <GuideList items={[
          '"Invalid company code or invite code" — verify both codes with your administrator; regenerated codes replace old ones.',
          '"Your session has expired" — simply sign in again with your codes; your saved progress is intact.',
          'A field will not accept my number — percentage fields are limited to 0–100.',
          'I cannot type in D2 — it is disabled because you answered "No" to D1.',
          'Data privacy — your individual responses are confidential; only aggregated, anonymised statistics are shared. See the Privacy Notice for details.',
        ]} />
      </>
    ),
  },
];

export default function RespondentGuidePage() {
  return (
    <GuideLayout
      title="Respondent User Guide"
      audience="For participating member organisations"
      subtitle="Everything you need to complete the IT-BPM ESG Double Materiality survey — from signing in with your invite codes to downloading your benchmark report."
      sections={sections}
      backTo="/questionnaire"
      backLabel="Back to Survey"
    />
  );
}
